import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Smartphone, 
  Globe, 
  Bitcoin, 
  Briefcase,
  ArrowRight,
  Code,
  ShoppingCart,
  Monitor,
  FileCode,
  Wallet,
  Cloud,
  TrendingUp,
  CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import CTABanner from "@/components/CTABanner";

const ServicesPage = () => {
  const services = [
    {
      id: "application-development",
      icon: Smartphone,
      title: "Application Development",
      description: "Custom mobile and enterprise applications tailored to your business needs. We build solutions that drive efficiency and engagement.",
      features: [
        "Custom Mobile Apps (iOS & Android)",
        "Enterprise Applications",
        "Cross-Platform Solutions",
        "Native App Development",
        "App Maintenance & Support",
        "UI/UX Design"
      ],
      gradient: "from-blue-600 to-blue-400"
    },
    {
      id: "web-development",
      icon: Globe,
      title: "Web Development",
      description: "Full-stack web solutions from responsive websites to complex platforms. Modern technologies for modern businesses.",
      features: [
        "Full Stack Development",
        "E-commerce Platforms",
        "Responsive Websites",
        "Progressive Web Apps",
        "CMS Development",
        "API Development"
      ],
      gradient: "from-cyan-500 to-blue-500"
    },
    {
      id: "cryptocurrency-development",
      icon: Bitcoin,
      title: "Cryptocurrency Development",
      description: "Comprehensive blockchain solutions including tokens, smart contracts, and DeFi applications. Secure and scalable.",
      features: [
        "Custom Token Creation",
        "Smart Contract Development",
        "DeFi Solutions",
        "Secure Wallet Development",
        "NFT Marketplace",
        "Blockchain Integration"
      ],
      gradient: "from-purple-600 to-blue-500"
    },
    {
      id: "it-consultancy",
      icon: Briefcase,
      title: "IT Consultancy",
      description: "Strategic technology consulting for digital transformation and sustainable growth. Expert guidance for your tech journey.",
      features: [
        "Business Tech Strategy",
        "Digital Transformation",
        "Cloud & Infrastructure",
        "Security Consulting",
        "Process Optimization",
        "Technology Assessment"
      ],
      gradient: "from-blue-500 to-cyan-400"
    }
  ];

  return (
    <div className="pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-600/10 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-blue-400 mb-4 block">
              Our Services
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-gradient leading-tight mb-6">
              Comprehensive IT Solutions
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed">
              From mobile apps to blockchain, we offer end-to-end technology services to transform your business.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="relative py-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 space-y-16">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              className={`grid lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? "lg:flex-row-reverse" : ""
              }`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              {/* Content */}
              <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6`}>
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  {service.title}
                </h2>
                <p className="text-lg text-slate-300 mb-6">
                  {service.description}
                </p>
                <ul className="grid sm:grid-cols-2 gap-3 mb-8">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-400">
                      <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to={`/services/${service.id}`}>
                  <Button
                    className="bg-blue-600 text-white rounded-full px-6 py-5 font-semibold hover:bg-blue-500 transition-all group"
                    data-testid={`service-learn-more-${service.id}`}
                  >
                    Learn More
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>

              {/* Visual */}
              <div className={`relative ${index % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="glass rounded-2xl p-8 sm:p-12 relative overflow-hidden">
                  {/* Background glow */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-10`} />
                  
                  {/* Icon grid */}
                  <div className="relative grid grid-cols-2 gap-6">
                    {[...Array(4)].map((_, idx) => (
                      <div
                        key={idx}
                        className="glass rounded-xl p-6 flex items-center justify-center"
                      >
                        <service.icon className={`w-10 h-10 text-slate-500 opacity-${50 + idx * 10}`} />
                      </div>
                    ))}
                  </div>

                  {/* Center icon */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center shadow-lg glow-blue`}>
                      <service.icon className="w-10 h-10 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Technologies Section */}
      <section className="relative py-24 sm:py-32 bg-[#0F172A]/50">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-blue-400 mb-4 block">
              Technologies
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Tools We Work With
            </h2>
            <p className="text-lg text-slate-300">
              We leverage cutting-edge technologies to build robust, scalable solutions.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              "React", "Node.js", "Python", "AWS", "MongoDB", "Solidity",
              "Flutter", "TypeScript", "PostgreSQL", "Docker", "Kubernetes", "GraphQL"
            ].map((tech, index) => (
              <motion.div
                key={tech}
                className="glass rounded-xl p-4 text-center glass-hover"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <span className="text-slate-300 font-medium">{tech}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
};

export default ServicesPage;
